// Bounded icon registry — the pure-JSON schema references icons by NAME and the
// engine resolves them here. Add a name to this map to make it usable from any
// sidebar config (keeps configs serializable, with no component references).

import {
  Activity,
  AlertTriangle,
  Archive,
  ArrowLeftRight,
  ArrowUpDown,
  BadgeCheck,
  BarChart2,
  BarChart3,
  Bell,
  Bot,
  Briefcase,
  Building2,
  Calendar,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  CheckSquare,
  CircleDot,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  Eye,
  FileStack,
  Flag,
  FolderKanban,
  FolderOpen,
  Gauge,
  Globe,
  GraduationCap,
  Grid3x3,
  Group,
  Hourglass,
  KeyRound,
  Layers,
  Link2,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  LayoutTemplate,
  Lightbulb,
  List,
  ListTodo,
  Lock,
  MapPin,
  MessageSquare,
  Monitor,
  Network,
  Package,
  Puzzle,
  Server,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Tag,
  Ticket,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  Video,
  Zap,
  BadgeDollarSign,
  Blocks,
  Coins,
  Contact,
  Database,
  FileSearch,
  FileSpreadsheet,
  FileText,
  Handshake,
  LineChart,
  Phone,
  RefreshCw,
  Target,
  Upload,
  UsersRound,
  Wallet,
  Cog,
  DollarSign,
  EyeOff,
  FileCheck,
  Fingerprint,
  HandCoins,
  ListTree,
  MessageSquareWarning,
  Paperclip,
  Percent,
  Receipt,
  ShieldAlert,
  TrendingUp,
  Trophy,
  Type,
  UserMinus,
  XCircle,
  UserRound,
  BookOpen,
  RotateCcw,
  CalendarCheck,
  Inbox,
  Workflow,
  Timer,
  CalendarMinus2,
  type LucideIcon,
} from 'lucide-react';

export const SIDEBAR_ICONS: Record<string, LucideIcon> = {
  Activity,
  AlertTriangle,
  Archive,
  ArrowLeftRight,
  ArrowUpDown,
  BadgeCheck,
  BarChart2,
  BarChart3,
  Bell,
  Bot,
  Briefcase,
  Building2,
  Calendar,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  CheckSquare,
  CircleDot,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  Eye,
  FileStack,
  Flag,
  FolderKanban,
  FolderOpen,
  Gauge,
  Globe,
  GraduationCap,
  Grid3x3,
  Group,
  Hourglass,
  KeyRound,
  Layers,
  Link2,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  LayoutTemplate,
  Lightbulb,
  List,
  ListTodo,
  Lock,
  MapPin,
  MessageSquare,
  Monitor,
  Network,
  Package,
  Puzzle,
  Server,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Tag,
  Ticket,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  Video,
  BadgeDollarSign,
  Blocks,
  Coins,
  Contact,
  Database,
  FileSearch,
  FileSpreadsheet,
  FileText,
  Phone,
  Handshake,
  LineChart,
  RefreshCw,
  Target,
  Upload,
  UsersRound,
  Wallet,
  Cog,
  DollarSign,
  EyeOff,
  FileCheck,
  Fingerprint,
  HandCoins,
  ListTree,
  MessageSquareWarning,
  Paperclip,
  Percent,
  Receipt,
  ShieldAlert,
  CalendarCheck,
  Inbox,
  Workflow,
  Timer,
  TrendingUp,
  Trophy,
  Type,
  UserMinus,
  XCircle,
  Zap,
  UserRound,
  BookOpen,
  RotateCcw,
  CalendarMinus2,
};

/** Resolve an icon name to a component, or undefined when unknown. */
export function resolveIcon(name: string | undefined): LucideIcon | undefined {
  if (!name) return undefined;
  return SIDEBAR_ICONS[name];
}

/**
 * Reverse lookup so existing constants that already store an icon COMPONENT
 * (SETTING_TABS, SUPPORT_SETTING_TABS) can be reused in a config without
 * hand-maintaining a second name map. Returns undefined for a component that
 * isn't registered — the row then renders without a leading icon rather than
 * crashing.
 */
const NAME_BY_ICON = new Map<LucideIcon, string>(
  Object.entries(SIDEBAR_ICONS).map(([name, comp]) => [comp, name]),
);

export function iconNameOf(icon: LucideIcon | undefined): string | undefined {
  return icon ? NAME_BY_ICON.get(icon) : undefined;
}
